// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — the funny names a meeting game gives its players
 * (`docs/AYNI_XR_MEETING_GAMES_PLAN.md` §2, decision D2): an earth-science
 * noun and a positive adjective, given by the room, never typed by anyone.
 * Kept as two word indices, so each viewer reads them in their own
 * language: "Glaciar Valiente", "Aurora Curiosa" (the adjective agrees with
 * the noun's gender), "Brave Glacier".
 *
 * The lists follow AYNI 3D's Maypi names (`geo-game/names.ts` there): no
 * disaster words (no volcano, monsoon, hail), since a word may touch a
 * child's own history, and pairs that read badly together are blocked.
 * The Spanish waits for a teacher's review before the first pilot class
 * (decision G8). Pure: shared by the browser and the rooms Worker.
 */

export type NameLocale = 'en' | 'es' | 'ca'

interface Noun {
  en: string
  es: string
  /** The Spanish noun's gender, for the adjective's agreement. */
  g: 'm' | 'f'
}

interface Adjective {
  en: string
  /** Masculine and feminine Spanish forms (the same word when it does not change). */
  m: string
  f: string
}

const n = (en: string, es: string, g: 'm' | 'f'): Noun => ({ en, es, g })
const a = (en: string, m: string, f: string = m): Adjective => ({ en, m, f })

/** Append only: a name is two indices, so reordering would rename everyone mid-game. */
export const NAME_NOUNS: readonly Noun[] = [
  n('Glacier', 'Glaciar', 'm'), n('Geyser', 'Géiser', 'm'), n('Fjord', 'Fiordo', 'm'), n('Aurora', 'Aurora', 'f'),
  n('Delta', 'Delta', 'm'), n('Dune', 'Duna', 'f'), n('Coral', 'Coral', 'm'), n('Canyon', 'Cañón', 'm'),
  n('Crater', 'Cráter', 'm'), n('Lagoon', 'Laguna', 'f'), n('Atoll', 'Atolón', 'm'), n('Basalt', 'Basalto', 'm'),
  n('Granite', 'Granito', 'm'), n('Quartz', 'Cuarzo', 'm'), n('Oasis', 'Oasis', 'm'), n('Peninsula', 'Península', 'f'),
  n('Prairie', 'Pradera', 'f'), n('Rainbow', 'Arcoíris', 'm'), n('Reef', 'Arrecife', 'm'), n('Savanna', 'Sabana', 'f'),
  n('Tundra', 'Tundra', 'f'), n('Taiga', 'Taiga', 'f'), n('Breeze', 'Brisa', 'f'), n('Drizzle', 'Llovizna', 'f'),
  n('Snowflake', 'Copo de Nieve', 'm'), n('Mangrove', 'Manglar', 'm'), n('Estuary', 'Estuario', 'm'), n('Waterfall', 'Cascada', 'f'),
  n('Meander', 'Meandro', 'm'), n('Fossil', 'Fósil', 'm'), n('Ammonite', 'Amonita', 'f'), n('Crystal', 'Cristal', 'm'),
  n('Geode', 'Geoda', 'f'), n('Pebble', 'Guijarro', 'm'), n('Summit', 'Cumbre', 'f'), n('Valley', 'Valle', 'm'),
  n('Equinox', 'Equinoccio', 'm'), n('Solstice', 'Solsticio', 'm'), n('Horizon', 'Horizonte', 'm'), n('Marble', 'Mármol', 'm'),
  n('Tide', 'Marea', 'f'), n('Stalactite', 'Estalactita', 'f'), n('Stalagmite', 'Estalagmita', 'f'), n('Cirrus', 'Cirro', 'm'),
  n('Plateau', 'Meseta', 'f'), n('Comet', 'Cometa', 'm'), n('Nebula', 'Nebulosa', 'f'), n('Lichen', 'Liquen', 'm'),
]

/** Append only, as the nouns. */
export const NAME_ADJECTIVES: readonly Adjective[] = [
  a('Swift', 'Veloz'), a('Curious', 'Curioso', 'Curiosa'), a('Brave', 'Valiente'), a('Bright', 'Brillante'),
  a('Calm', 'Tranquilo', 'Tranquila'), a('Clever', 'Listo', 'Lista'), a('Bold', 'Audaz'), a('Cheerful', 'Alegre'),
  a('Gentle', 'Amable'), a('Happy', 'Feliz'), a('Jolly', 'Risueño', 'Risueña'), a('Kind', 'Bondadoso', 'Bondadosa'),
  a('Lively', 'Vivaz'), a('Lucky', 'Afortunado', 'Afortunada'), a('Mighty', 'Poderoso', 'Poderosa'), a('Nimble', 'Ágil'),
  a('Patient', 'Paciente'), a('Playful', 'Juguetón', 'Juguetona'), a('Proud', 'Orgulloso', 'Orgullosa'), a('Quick', 'Rápido', 'Rápida'),
  a('Radiant', 'Radiante'), a('Shiny', 'Reluciente'), a('Sparkling', 'Chispeante'), a('Steady', 'Constante'),
  a('Sunny', 'Soleado', 'Soleada'), a('Wise', 'Sabio', 'Sabia'), a('Witty', 'Ingenioso', 'Ingeniosa'), a('Cosmic', 'Cósmico', 'Cósmica'),
  a('Dazzling', 'Deslumbrante'), a('Fearless', 'Intrépido', 'Intrépida'), a('Friendly', 'Simpático', 'Simpática'), a('Glowing', 'Luminoso', 'Luminosa'),
  a('Golden', 'Dorado', 'Dorada'), a('Noble', 'Noble'), a('Magical', 'Mágico', 'Mágica'), a('Stellar', 'Estelar'),
  a('Dreamy', 'Soñador', 'Soñadora'), a('Hardy', 'Resistente'), a('Loyal', 'Leal'), a('Serene', 'Sereno', 'Serena'),
  a('Daring', 'Atrevido', 'Atrevida'), a('Clear', 'Claro', 'Clara'),
]

/**
 * AYNI (2026-10-11): the Catalan words, by the same positions (append with
 * the lists above): the noun and its gender, and the adjective's masculine
 * and feminine forms. Noun then adjective, as in Spanish.
 */
export const NAME_NOUNS_CA: ReadonlyArray<readonly [word: string, gender: 'm' | 'f']> = [
  ['Glacera', 'f'], ['Guèiser', 'm'], ['Fiord', 'm'], ['Aurora', 'f'],
  ['Delta', 'm'], ['Duna', 'f'], ['Corall', 'm'], ['Canyó', 'm'],
  ['Cràter', 'm'], ['Llacuna', 'f'], ['Atol', 'm'], ['Basalt', 'm'],
  ['Granit', 'm'], ['Quars', 'm'], ['Oasi', 'm'], ['Península', 'f'],
  ['Prada', 'f'], ['Arc de Sant Martí', 'm'], ['Escull', 'm'], ['Sabana', 'f'],
  ['Tundra', 'f'], ['Taigà', 'f'], ['Brisa', 'f'], ['Plugim', 'm'],
  ['Floc de Neu', 'm'], ['Manglar', 'm'], ['Estuari', 'm'], ['Cascada', 'f'],
  ['Meandre', 'm'], ['Fòssil', 'm'], ['Ammonit', 'm'], ['Cristall', 'm'],
  ['Geoda', 'f'], ['Còdol', 'm'], ['Cim', 'm'], ['Vall', 'f'],
  ['Equinocci', 'm'], ['Solstici', 'm'], ['Horitzó', 'm'], ['Marbre', 'm'],
  ['Marea', 'f'], ['Estalactita', 'f'], ['Estalagmita', 'f'], ['Cirrus', 'm'],
  ['Altiplà', 'm'], ['Cometa', 'm'], ['Nebulosa', 'f'], ['Liquen', 'm'],
]

export const NAME_ADJECTIVES_CA: ReadonlyArray<readonly [masculine: string, feminine: string]> = [
  ['Veloç', 'Veloç'], ['Curiós', 'Curiosa'], ['Valent', 'Valenta'], ['Brillant', 'Brillant'],
  ['Tranquil', 'Tranquil·la'], ['Llest', 'Llesta'], ['Audaç', 'Audaç'], ['Alegre', 'Alegre'],
  ['Amable', 'Amable'], ['Feliç', 'Feliç'], ['Rialler', 'Riallera'], ['Bondadós', 'Bondadosa'],
  ['Vivaç', 'Vivaç'], ['Afortunat', 'Afortunada'], ['Poderós', 'Poderosa'], ['Àgil', 'Àgil'],
  ['Pacient', 'Pacient'], ['Juganer', 'Juganera'], ['Orgullós', 'Orgullosa'], ['Ràpid', 'Ràpida'],
  ['Radiant', 'Radiant'], ['Lluent', 'Lluent'], ['Espurnejant', 'Espurnejant'], ['Constant', 'Constant'],
  ['Assolellat', 'Assolellada'], ['Savi', 'Sàvia'], ['Enginyós', 'Enginyosa'], ['Còsmic', 'Còsmica'],
  ['Enlluernador', 'Enlluernadora'], ['Intrèpid', 'Intrèpida'], ['Simpàtic', 'Simpàtica'], ['Lluminós', 'Lluminosa'],
  ['Daurat', 'Daurada'], ['Noble', 'Noble'], ['Màgic', 'Màgica'], ['Estel·lar', 'Estel·lar'],
  ['Somiador', 'Somiadora'], ['Resistent', 'Resistent'], ['Lleial', 'Lleial'], ['Serè', 'Serena'],
  ['Agosarat', 'Agosarada'], ['Clar', 'Clara'],
]

/** A name: [adjective index, noun index]. */
export type GameName = [adjective: number, noun: number]

/** Pairs that read badly together ("adjective:noun", by their English words); short on purpose, the review extends it. */
const BLOCKED: ReadonlySet<string> = new Set(['Dreamy:Fossil', 'Hardy:Snowflake'])

/** Whether a name is one of the lists' and allowed. */
export function isGameName(raw: unknown): raw is GameName {
  if (!Array.isArray(raw) || raw.length !== 2) return false
  const [adj, noun] = raw as unknown[]
  if (!Number.isInteger(adj) || !Number.isInteger(noun)) return false
  const ai = adj as number
  const ni = noun as number
  if (ai < 0 || ai >= NAME_ADJECTIVES.length || ni < 0 || ni >= NAME_NOUNS.length) return false
  return !BLOCKED.has(`${NAME_ADJECTIVES[ai].en}:${NAME_NOUNS[ni].en}`)
}

/** A name in the viewer's language: noun then adjective in Spanish, adjective then noun in English. */
export function renderGameName(name: GameName, locale: string): string {
  const adj = NAME_ADJECTIVES[name[0]]
  const noun = NAME_NOUNS[name[1]]
  if (!adj || !noun) return ''
  if (locale.startsWith('es')) return `${noun.es} ${noun.g === 'f' ? adj.f : adj.m}`
  if (locale.startsWith('ca')) {
    const nounCa = NAME_NOUNS_CA[name[1]]
    const adjCa = NAME_ADJECTIVES_CA[name[0]]
    if (nounCa && adjCa) return `${nounCa[0]} ${nounCa[1] === 'f' ? adjCa[1] : adjCa[0]}`
  }
  return `${adj.en} ${noun.en}`
}

/**
 * A fresh name not in `taken` (keys `"a:n"`). `random` returns [0, 1).
 * Tries random pairs first, then walks the lists, so a full meeting still
 * gets one; null only when every pair is taken.
 */
export function pickGameName(taken: ReadonlySet<string>, random: () => number = Math.random): GameName | null {
  const ok = (name: GameName): boolean => isGameName(name) && !taken.has(`${name[0]}:${name[1]}`)
  for (let i = 0; i < 40; i++) {
    const name: GameName = [Math.floor(random() * NAME_ADJECTIVES.length), Math.floor(random() * NAME_NOUNS.length)]
    if (ok(name)) return name
  }
  for (let ai = 0; ai < NAME_ADJECTIVES.length; ai++) {
    for (let ni = 0; ni < NAME_NOUNS.length; ni++) if (ok([ai, ni])) return [ai, ni]
  }
  return null
}

/** The key a name is kept under in a set of names taken. */
export const nameKey = (name: GameName): string => `${name[0]}:${name[1]}`
